/**
 * ChatMessage Component
 * Renders individual chat messages with markdown support and type-specific styling
 */

import React, { useState } from 'react';

/**
 * Copy icon
 */
function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
    </svg>
  );
}

/**
 * Check icon (for copied state)
 */
function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  );
}

/**
 * Parse markdown-like content to React elements
 * Handles: bold, inline code, code blocks, lists, headers, links
 */
function parseMarkdown(text, isStreaming = false) {
  if (!text) return null;

  const elements = [];
  let key = 0;

  // Split by code blocks first
  const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;

  while ((match = codeBlockRegex.exec(text)) !== null) {
    // Add text before code block
    if (match.index > lastIndex) {
      const beforeText = text.slice(lastIndex, match.index);
      elements.push(
        <span key={key++}>
          {parseInlineMarkdown(beforeText)}
        </span>
      );
    }

    // Add code block
    const language = match[1] || 'text';
    const code = match[2];
    elements.push(
      <CodeBlock key={key++} language={language} code={code} />
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    elements.push(
      <span key={key++}>
        {parseInlineMarkdown(remainingText)}
        {isStreaming && <span className="chatbot-streaming-cursor"></span>}
      </span>
    );
  } else if (isStreaming) {
    elements.push(<span key={key++} className="chatbot-streaming-cursor"></span>);
  }

  return elements;
}

/**
 * Parse inline markdown (bold, code, links, lists, headers)
 */
function parseInlineMarkdown(text) {
  if (!text) return null;

  // Split by lines for block-level parsing
  const lines = text.split('\n');
  const elements = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Check for headers
    const h3Match = line.match(/^### (.+)/);
    const h2Match = line.match(/^## (.+)/);
    const h1Match = line.match(/^# (.+)/);

    if (h3Match) {
      elements.push(<h3 key={i}>{parseInlineElements(h3Match[1])}</h3>);
      continue;
    }
    if (h2Match) {
      elements.push(<h2 key={i}>{parseInlineElements(h2Match[1])}</h2>);
      continue;
    }
    if (h1Match) {
      elements.push(<h1 key={i}>{parseInlineElements(h1Match[1])}</h1>);
      continue;
    }

    // Check for unordered list items
    const ulMatch = line.match(/^[-*] (.+)/);
    if (ulMatch) {
      // Collect consecutive list items
      const items = [ulMatch[1]];
      while (i + 1 < lines.length && lines[i + 1].match(/^[-*] /)) {
        i++;
        items.push(lines[i].replace(/^[-*] /, ''));
      }
      elements.push(
        <ul key={i}>
          {items.map((item, idx) => (
            <li key={idx}>{parseInlineElements(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Check for ordered list items
    const olMatch = line.match(/^\d+\. (.+)/);
    if (olMatch) {
      // Collect consecutive numbered items
      const items = [olMatch[1]];
      while (i + 1 < lines.length && lines[i + 1].match(/^\d+\. /)) {
        i++;
        items.push(lines[i].replace(/^\d+\. /, ''));
      }
      elements.push(
        <ol key={i}>
          {items.map((item, idx) => (
            <li key={idx}>{parseInlineElements(item)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Regular paragraph
    if (line.trim()) {
      elements.push(<p key={i}>{parseInlineElements(line)}</p>);
    } else if (i < lines.length - 1) {
      // Add spacing for empty lines (but not trailing)
      elements.push(<br key={i} />);
    }
  }

  return elements;
}

/**
 * Parse inline elements (images, bold, code, links)
 */
function parseInlineElements(text) {
  if (!text) return null;

  const result = [];
  let key = 0;

  // Pattern for images, bold, inline code, and links.
  // The image pattern must be matched before the link pattern strips its
  // [alt](src) core — overlap handling below keeps the earlier match.
  const patterns = [
    { regex: /!\[([^\]]*)\]\(([^)]+)\)/g, render: (match) => (
      <span key={key++} className="chatbot-md-figure">
        <img src={match[2]} alt={match[1]} loading="lazy" />
        {match[1] && <span className="chatbot-md-figure-caption">{match[1]}</span>}
      </span>
    )},
    { regex: /\*\*(.+?)\*\*/g, render: (match) => <strong key={key++}>{match[1]}</strong> },
    { regex: /`([^`]+)`/g, render: (match) => <code key={key++}>{match[1]}</code> },
    { regex: /\[([^\]]+)\]\(([^)]+)\)/g, render: (match) => (
      <a key={key++} href={match[2]} target="_blank" rel="noopener noreferrer">
        {match[1]}
      </a>
    )}
  ];

  // Process text sequentially
  let processedIndex = 0;
  const allMatches = [];

  // Collect all matches
  for (const pattern of patterns) {
    let match;
    const regex = new RegExp(pattern.regex.source, 'g');
    while ((match = regex.exec(text)) !== null) {
      allMatches.push({
        index: match.index,
        length: match[0].length,
        match: match,
        render: pattern.render
      });
    }
  }

  // Sort matches by position
  allMatches.sort((a, b) => a.index - b.index);

  // Build result
  for (const m of allMatches) {
    // Skip overlapping matches
    if (m.index < processedIndex) continue;

    // Add text before match
    if (m.index > processedIndex) {
      result.push(text.slice(processedIndex, m.index));
    }

    // Add rendered match
    result.push(m.render(m.match));
    processedIndex = m.index + m.length;
  }

  // Add remaining text
  if (processedIndex < text.length) {
    result.push(text.slice(processedIndex));
  }

  return result.length > 0 ? result : text;
}

/**
 * Code block with copy functionality
 */
function CodeBlock({ language, code }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="chatbot-code-block">
      <pre>
        <code className={`language-${language}`}>{code}</code>
      </pre>
      <button 
        className="chatbot-code-copy" 
        onClick={handleCopy}
        type="button"
        aria-label={copied ? 'Copied' : 'Copy code'}
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/**
 * Format timestamp for display
 */
function formatTime(timestamp) {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  
  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  
  return date.toLocaleDateString([], { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit', 
    minute: '2-digit' 
  });
}

/**
 * Main ChatMessage component
 */
function ChatMessage({ message, isStreaming = false }) {
  const { role, content, timestamp } = message;
  const isUser = role === 'user';

  return (
    <div className={`chatbot-message ${role}`}>
      <div className="chatbot-message-content">
        {isUser ? (
          // User messages are plain text
          <p>{content}</p>
        ) : (
          // Assistant messages get markdown parsing
          parseMarkdown(content, isStreaming)
        )}
      </div>
      {timestamp && (
        <div className="chatbot-message-time">
          {formatTime(timestamp)}
        </div>
      )}
    </div>
  );
}

export default ChatMessage;
